
import { supabase } from './supabaseClient';
import { Empresa, UserRole } from '../types';

export const empresasService = {
  async getEmpresas(): Promise<Empresa[]> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*, planos(*)')
      .order('created_at', { ascending: false });
    if (error) throw error;
    
    return (data || []).map(emp => {
      const plan = emp.planos ? (Array.isArray(emp.planos) ? emp.planos[0] : emp.planos) : null;
      return {
        ...emp,
        plano: (plan?.slug || emp.plano || 'ESSENCIAL').toUpperCase(),
        permite_relatorios: plan?.permite_relatorios ?? emp.permite_relatorios ?? false,
        permite_dashboard_avancado: plan?.permite_dashboard_avancado ?? emp.permite_dashboard_avancado ?? false
      };
    });
  },

  async criarEmpresa(empresa: Partial<Empresa>, adminEmail: string, adminSenha: string) {
    // 1. Criar a empresa
    const payloadEmpresa = {
      nome: empresa.nome,
      slug: empresa.slug,
      descricao: empresa.descricao || '',
      telefone_whatsapp: empresa.telefone_whatsapp,
      plano: (empresa.plano || 'essencial').toLowerCase(),
      status: 'ativo'
    };

    const { data: novaEmpresa, error: empError } = await supabase
      .from('empresas')
      .insert([payloadEmpresa])
      .select()
      .single();

    if (empError) throw empError;

    // 2. Criar o usuário no Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: adminEmail,
      password: adminSenha,
      options: {
        data: {
          nome: `Admin ${empresa.nome}`,
          role: 'ADMIN' // Role inicial no metadata
        }
      }
    });

    if (authError) throw authError;

    if (authData.user) {
      // 3. Criar o vínculo na empresa_usuarios
      const { error: vincError } = await supabase.from('empresa_usuarios').insert([{
        user_id: authData.user.id,
        empresa_id: novaEmpresa.id,
        role: 'OWNER', // Dono da empresa
        ativo: true
      }]);

      if (vincError) throw vincError;
    }

    return novaEmpresa;
  },

  async atualizarEmpresa(id: string, empresa: Partial<Empresa>) {
    const whitelist = [
      'nome', 'slug', 'descricao', 'horarios', 'telefone_whatsapp', 
      'plano', 'status', 'logo_url', 'cor_primaria', 
      'permite_relatorios', 'permite_dashboard_avancado'
    ];

    const dadosParaAtualizar: any = {};
    whitelist.forEach(key => {
      if ((empresa as any)[key] !== undefined) {
        dadosParaAtualizar[key] = (empresa as any)[key];
      }
    });
    
    const { error } = await supabase
      .from('empresas')
      .update(dadosParaAtualizar)
      .eq('id', id);
      
    if (error) {
      console.error("Erro Supabase:", error);
      throw new Error(`Erro ao atualizar empresa: ${error.message}`);
    }
  },

  async deletarEmpresa(id: string) {
    const { error } = await supabase.from('empresas').delete().eq('id', id);
    if (error) throw error;
  },

  async getEmpresaBySlug(slug: string): Promise<Empresa | null> {
    const { data, error } = await supabase
      .from('empresas')
      .select('*, planos(*)')
      .eq('slug', slug)
      .eq('status', 'ativo')
      .maybeSingle();

    if (error || !data) return null;

    const plan = data.planos ? (Array.isArray(data.planos) ? data.planos[0] : data.planos) : null;
    return {
      ...data,
      plano: (plan?.slug || data.plano || 'ESSENCIAL').toUpperCase(),
      permite_relatorios: plan?.permite_relatorios ?? data.permite_relatorios ?? false,
      permite_dashboard_avancado: plan?.permite_dashboard_avancado ?? data.permite_dashboard_avancado ?? false
    };
  }
};
